/** Payables by date range, grouped as needed (§4.9B). */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download } from 'lucide-react';
import { api, getAccessToken } from '../../lib/api';
import { peso, shortDate } from '../../lib/format';
import {
  Button,
  Card,
  EmptyState,
  Field,
  Skeleton,
  StatusChip,
  TableWrap,
  Td,
  Th,
  inputClass,
} from '../../components/ui';

interface ApRow {
  id: string;
  code: string;
  entryDate: string;
  paidDate: string | null;
  payee: string;
  description: string | null;
  amount: string;
  status: 'PENDING' | 'COMPLETED' | 'DISAPPROVED' | 'CANCELLED';
  chargeType: 'PROJECT' | 'ACCOUNT';
  project: { code: string; name: string } | null;
  chargeAccount: { label: string } | null;
  requester: { name: string };
}

interface Report {
  generatedBy: string;
  generatedAt: string;
  basis: string;
  groups: { key: string; rows: ApRow[]; subtotal: string }[];
  grandTotal: string;
  count: number;
}

interface ProjectOption {
  id: string;
  code: string;
  name: string;
}

const startOfMonth = (): string => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
};

export function ApReportPage(): JSX.Element {
  const [filters, setFilters] = useState({
    from: startOfMonth(),
    to: new Date().toISOString().slice(0, 10),
    basis: 'ENTRY' as 'ENTRY' | 'PAID',
    status: '',
    chargeType: '',
    projectId: '',
    groupBy: 'none' as 'none' | 'project' | 'account' | 'payee',
  });

  const params = new URLSearchParams({
    from: filters.from,
    to: filters.to,
    basis: filters.basis,
    groupBy: filters.groupBy,
  });
  if (filters.status) params.set('status', filters.status);
  if (filters.chargeType) params.set('chargeType', filters.chargeType);
  if (filters.projectId) params.set('projectId', filters.projectId);

  const report = useQuery({
    queryKey: ['ap-report', params.toString()],
    queryFn: () => api<{ data: Report }>(`/reports/ap?${params.toString()}`),
  });
  const projects = useQuery({
    queryKey: ['projects', 'options'],
    queryFn: () => api<{ data: ProjectOption[] }>('/projects?pageSize=200'),
  });

  const exportExcel = async (): Promise<void> => {
    const response = await fetch(`/api/v1/reports/ap/export?${params.toString()}`, {
      credentials: 'include',
      headers: { authorization: `Bearer ${getAccessToken() ?? ''}` },
    });
    if (!response.ok) return;
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `ITCORE-SF_Payables_${filters.from}_${filters.to}.xlsx`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const data = report.data?.data;

  return (
    <div className="mx-auto max-w-7xl space-y-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-navy dark:text-white">Payables report</h1>
          <p className="text-sm text-slate-500">
            What we spent, by date range, grouped however you need it.
          </p>
        </div>
        <Button variant="secondary" onClick={() => void exportExcel()}>
          <Download aria-hidden="true" className="h-4 w-4" /> Export Excel
        </Button>
      </header>

      <Card>
        <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
          <Field label="From">
            <input
              className={inputClass}
              type="date"
              value={filters.from}
              onChange={(event) => setFilters({ ...filters, from: event.target.value })}
            />
          </Field>
          <Field label="To">
            <input
              className={inputClass}
              type="date"
              value={filters.to}
              onChange={(event) => setFilters({ ...filters, to: event.target.value })}
            />
          </Field>
          <Field label="Dates are">
            <select
              className={inputClass}
              value={filters.basis}
              onChange={(event) =>
                setFilters({ ...filters, basis: event.target.value as 'ENTRY' | 'PAID' })
              }
            >
              <option value="ENTRY">Entry dates</option>
              <option value="PAID">Paid dates</option>
            </select>
          </Field>
          <Field label="Status">
            <select
              className={inputClass}
              value={filters.status}
              onChange={(event) => setFilters({ ...filters, status: event.target.value })}
            >
              <option value="">All</option>
              <option value="PENDING">Pending</option>
              <option value="COMPLETED">Completed</option>
              <option value="DISAPPROVED">Disapproved</option>
            </select>
          </Field>
          <Field label="Charged to">
            <select
              className={inputClass}
              value={filters.chargeType}
              onChange={(event) => setFilters({ ...filters, chargeType: event.target.value })}
            >
              <option value="">Any</option>
              <option value="PROJECT">Projects</option>
              <option value="ACCOUNT">Charge accounts</option>
            </select>
          </Field>
          <Field label="Group by">
            <select
              className={inputClass}
              value={filters.groupBy}
              onChange={(event) =>
                setFilters({ ...filters, groupBy: event.target.value as typeof filters.groupBy })
              }
            >
              <option value="none">Nothing</option>
              <option value="project">Project</option>
              <option value="account">Charge account</option>
              <option value="payee">Payee</option>
            </select>
          </Field>
        </div>
        {filters.chargeType === 'PROJECT' && (
          <div className="mt-3 max-w-sm">
            <Field label="Project">
              <select
                className={inputClass}
                value={filters.projectId}
                onChange={(event) => setFilters({ ...filters, projectId: event.target.value })}
              >
                <option value="">All projects</option>
                {projects.data?.data.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.code} — {project.name}
                  </option>
                ))}
              </select>
            </Field>
          </div>
        )}
      </Card>

      {report.isLoading && <Skeleton className="h-64 w-full" />}

      {data && data.count === 0 && (
        <Card>
          <EmptyState
            title="Nothing in that range"
            description="Try a wider date range, or switch between entry and paid dates."
          />
        </Card>
      )}

      {data && data.count > 0 && (
        <>
          <div className="grid gap-4 sm:grid-cols-3">
            <Card>
              <p className="text-xs uppercase tracking-wide text-slate-500">Entries</p>
              <p className="tabular text-lg font-bold">{data.count}</p>
            </Card>
            <Card>
              <p className="text-xs uppercase tracking-wide text-slate-500">Basis</p>
              <p className="text-lg font-bold">{data.basis}</p>
            </Card>
            <Card className="bg-navy text-white dark:bg-navy">
              <p className="text-xs uppercase tracking-wide text-white/70">Grand total</p>
              <p className="tabular text-lg font-bold">{peso(data.grandTotal)}</p>
            </Card>
          </div>

          {data.groups.map((group) => (
            <Card
              key={group.key}
              title={group.key}
              description={`${group.rows.length} entr${group.rows.length === 1 ? 'y' : 'ies'} · subtotal ${peso(group.subtotal)}`}
            >
              <TableWrap>
                <thead>
                  <tr>
                    <Th>Entry</Th>
                    <Th>Payee</Th>
                    <Th>Charged to</Th>
                    <Th>Requested by</Th>
                    <Th>Status</Th>
                    <Th align="right">Amount</Th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {group.rows.map((row) => (
                    <tr key={row.id}>
                      <Td>
                        <p className="font-mono text-xs">{row.code}</p>
                        <p className="text-xs text-slate-500">
                          {shortDate(row.entryDate)}
                          {row.paidDate ? ` · paid ${shortDate(row.paidDate)}` : ''}
                        </p>
                      </Td>
                      <Td>
                        <p>{row.payee}</p>
                        {row.description && (
                          <p className="text-xs text-slate-500">{row.description}</p>
                        )}
                      </Td>
                      <Td>
                        {row.chargeType === 'PROJECT'
                          ? (row.project?.name ?? '—')
                          : (row.chargeAccount?.label ?? '—')}
                      </Td>
                      <Td>{row.requester.name}</Td>
                      <Td>
                        <StatusChip
                          tone={
                            row.status === 'COMPLETED'
                              ? 'ok'
                              : row.status === 'DISAPPROVED'
                                ? 'critical'
                                : 'warning'
                          }
                        >
                          {row.status.charAt(0) + row.status.slice(1).toLowerCase()}
                        </StatusChip>
                      </Td>
                      <Td align="right" className="tabular font-semibold">
                        {peso(row.amount)}
                      </Td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-slate-200 font-bold dark:border-slate-700">
                    <Td>Subtotal</Td>
                    <Td>{''}</Td>
                    <Td>{''}</Td>
                    <Td>{''}</Td>
                    <Td>{''}</Td>
                    <Td align="right" className="tabular">
                      {peso(group.subtotal)}
                    </Td>
                  </tr>
                </tfoot>
              </TableWrap>
            </Card>
          ))}

          <p className="text-xs text-slate-500">
            Generated by {data.generatedBy} on {shortDate(data.generatedAt)}.
          </p>
        </>
      )}
    </div>
  );
}
